<template>
  <el-container class="app-container">
    <!-- Header -->
    <el-header class="app-header" height="56px">
      <div class="header-brand">
        <el-icon size="24"><Document /></el-icon>
        <span class="brand-text">Мониторинг жалоб</span>
      </div>
      <el-menu
        :default-active="$route.path"
        mode="horizontal"
        :router="true"
        class="header-menu"
      >
        <el-menu-item index="/">
          <el-icon><DataLine /></el-icon>
          <span>Обзор</span>
        </el-menu-item>
        <el-menu-item index="/posts">
          <el-icon><Document /></el-icon>
          <span>Посты</span>
        </el-menu-item>
        <el-menu-item index="/sources">
          <el-icon><Monitor /></el-icon>
          <span>Источники</span>
        </el-menu-item>
        <el-menu-item index="/reports">
          <el-icon><Download /></el-icon>
          <span>Отчёты</span>
        </el-menu-item>
        <el-menu-item v-if="authStore.canCreateManualPost" index="/manual-add">
          <el-icon><Plus /></el-icon>
          <span>Добавить пост</span>
        </el-menu-item>
      </el-menu>

      <div class="header-actions">
        <el-tooltip :content="themeStore.theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'">
          <el-button
            class="theme-toggle"
            circle
            :aria-label="themeStore.theme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'"
            @click="themeStore.toggleTheme"
          >
            <el-icon><Sunny v-if="themeStore.theme === 'dark'" /><Moon v-else /></el-icon>
          </el-button>
        </el-tooltip>

        <el-tag v-if="authStore.isAuthenticated" :type="healthTagType" size="small" effect="light">
          <el-icon v-if="statsStore.isHealthy"><CircleCheck /></el-icon>
          <el-icon v-else-if="statsStore.isDegraded"><Warning /></el-icon>
          <el-icon v-else><CircleClose /></el-icon>
          {{ healthText }}
        </el-tag>

        <template v-if="authStore.isAuthenticated">
          <el-dropdown trigger="click" @command="handleCommand">
            <div class="user-block">
              <el-avatar :size="28" :icon="UserFilled" />
              <span class="user-email">{{ authStore.user?.email }}</span>
              <el-tag
                :type="roleTagType"
                size="small"
                effect="plain"
                class="role-badge"
              >
                {{ roleLabel }}
              </el-tag>
              <el-icon><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item disabled>
                  {{ authStore.user?.email }}
                </el-dropdown-item>
                <el-dropdown-item disabled>
                  Роль: {{ roleLabel }}
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  Выйти
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </div>
    </el-header>

    <!-- Main Content -->
    <el-main class="app-main">
      <div v-if="authStore.isLoading" class="loading-container">
        <el-icon size="32" class="is-loading"><Loading /></el-icon>
        <p>Загрузка...</p>
      </div>
      <router-view v-else v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </el-main>
  </el-container>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useStatsStore } from '@/stores/stats'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import {
  UserFilled,
  ArrowDown,
  SwitchButton,
  Loading,
} from '@element-plus/icons-vue'

const statsStore = useStatsStore()
const authStore = useAuthStore()
const themeStore = useThemeStore()

const healthTagType = computed(() => {
  if (statsStore.isHealthy) return 'success'
  if (statsStore.isDegraded) return 'warning'
  return 'danger'
})

const healthText = computed(() => {
  if (statsStore.isHealthy) return 'Работает'
  if (statsStore.isDegraded) return 'Деградация'
  return 'Ошибка'
})

const roleLabel = computed(() => {
  switch (authStore.user?.role) {
    case 'admin': return 'Администратор'
    case 'editor': return 'Редактор'
    case 'reader': return 'Читатель'
    default: return ''
  }
})

const roleTagType = computed(() => {
  switch (authStore.user?.role) {
    case 'admin': return 'danger'
    case 'editor': return 'success'
    case 'reader': return 'info'
    default: return ''
  }
})

function handleCommand(command: string) {
  if (command === 'logout') {
    authStore.logout()
  }
}

onMounted(() => {
  if (authStore.isAuthenticated) {
    statsStore.loadHealth()
  }
})
</script>

<style scoped>
.app-container {
  min-height: 100vh;
  background-color: var(--app-bg-page);
  color: var(--app-text-primary);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--app-bg-surface);
  border-bottom: 1px solid var(--app-border-color);
  padding: 0 20px;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 700;
  color: var(--app-text-primary);
}

.brand-text {
  background: linear-gradient(135deg, #409eff 0%, #67c23a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-menu {
  flex: 1;
  margin-left: 32px;
  border-bottom: none;
  background-color: transparent;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-block {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.user-block:hover {
  background-color: var(--app-bg-muted);
}

.user-email {
  font-size: 13px;
  font-weight: 500;
  color: var(--app-text-regular);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.role-badge {
  font-size: 11px;
}

.app-main {
  padding: 16px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 16px;
  color: var(--app-text-secondary);
}

.is-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
